// apps/web/src/hooks/useAuth.tsx
import React, { createContext, useContext, useEffect, useState } from "react";

const API = import.meta.env.VITE_API_URL || "https://api.runwaycrew.com";

type Brand = { id: string; name: string; role?: string };
type User = { id: string; email: string; name?: string; role?: string };

type AuthContextType = {
  user: User | null;
  brands: Brand[];
  loading: boolean;
  loginWithCredentials: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  brands: [],
  loading: true,
  loginWithCredentials: async () => false,
  logout: async () => {},
  refresh: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const res = await fetch(`${API}/auth/session`, { credentials: "include" });
      const data = await res.json();
      setUser(data.user || null);
      setBrands(Array.isArray(data.brands) ? data.brands : []);
    } catch {
      setUser(null);
      setBrands([]);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await refresh();
      setLoading(false);
    })();
  }, []);

  const loginWithCredentials = async (email: string, password: string) => {
    const res = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    setUser(data.user);
    setBrands(Array.isArray(data.brands) ? data.brands : []);
    return true;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } finally {
      setUser(null);
      setBrands([]);
    }
  };

  return (
    <AuthContext.Provider value={{ user, brands, loading, loginWithCredentials, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
